import { useEffect, useState } from "react";
import api, { formatErr } from "../../../lib/api";
import { card, btn, btnGhost, label, inputStyle, Card } from "./_genCommon";
import MicButton from "../../MicButton";

/** Step 3: visual cards + matrix + AI refine + adopt + hybrid + side-by-side. */
export default function CompareStep({
  alts, weights, onSelect, selected,
  activeAlt, setActiveAlt,
  loadReport, reportMd, onBack,
  projectId, onAltsRefreshed,
  budgetMax, monthsMax, onDownloadPdf,
  onOpenFullView, onOpenHybrid, onOpenSxs,
}) {
  const [sortKey, setSortKey] = useState("weighted_total");
  const [filter, setFilter] = useState("all");
  const [thumbs, setThumbs] = useState({});
  const [refineCmd, setRefineCmd] = useState("");
  const [refining, setRefining] = useState(false);
  const [refineLog, setRefineLog] = useState([]);
  const [refineErr, setRefineErr] = useState("");

  // Lazy-load thumbnails for each alt id seen
  useEffect(() => {
    let cancel = false;
    (async () => {
      const out = {};
      for (const a of alts) {
        if (thumbs[a.id]) continue;
        try {
          const r = await api.get(`/projects/${projectId}/generative/thumbnail/${a.id}.svg`);
          if (cancel) return;
          out[a.id] = r.data.svg;
        } catch (_) { /* ignore */ }
      }
      if (!cancel && Object.keys(out).length) {
        setThumbs(prev => ({ ...prev, ...out }));
      }
    })();
    return () => { cancel = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alts.map(a => a.id).join(","), projectId]);

  const runRefine = async (cmdOverride) => {
    const cmd = (cmdOverride ?? refineCmd).trim();
    if (!cmd) return;
    setRefining(true); setRefineErr("");
    try {
      const r = await api.post(`/projects/${projectId}/generative/refine`,
          { command: cmd, alt_id: activeAlt?.id });
      setRefineLog(prev => [{
        cmd: cmd, alt_id: r.data.alt_id,
        mutations: r.data.mutations || [],
        explanation: r.data.explanation || "",
        ts: new Date().toLocaleTimeString(),
      }, ...prev]);
      setRefineCmd("");
      onAltsRefreshed?.(r.data.alternatives || []);
      setThumbs({});
    } catch (e) {
      setRefineErr(formatErr(e?.response?.data?.detail) || String(e));
    }
    setRefining(false);
  };

  const sorted = [...alts].sort((a, b) => {
    const v1 = sortKey === "weighted_total" ? a.scores?.weighted_total ?? 0
                  : sortKey === "capex" ? a.cost?.capex_INR_cr ?? 0
                  : a.schedule?.months ?? 0;
    const v2 = sortKey === "weighted_total" ? b.scores?.weighted_total ?? 0
                  : sortKey === "capex" ? b.cost?.capex_INR_cr ?? 0
                  : b.schedule?.months ?? 0;
    return sortKey === "weighted_total" ? v2 - v1 : v1 - v2;
  });
  const filtered = sorted.filter(a => {
    if (filter === "all") return true;
    if (filter === "within_budget") return (a.cost?.capex_INR_cr ?? 0) <= (budgetMax ?? 1e9);
    if (filter === "within_schedule") return (a.schedule?.months ?? 0) <= (monthsMax ?? 999);
    if (filter === "top_3") return sorted.indexOf(a) < 3;
    if (filter === "hybrids") return String(a.id).startsWith("ALT-H");
    return true;
  });

  const minMax = (getter) => {
    const vals = alts.map(getter);
    return [Math.min(...vals), Math.max(...vals)];
  };
  const [capLo, capHi] = minMax(a => a.cost?.capex_INR_cr ?? 0);
  const [mLo, mHi] = minMax(a => a.schedule?.months ?? 0);
  const cellColor = (v, lo, hi, lowBetter = true) => {
    if (lo === hi) return undefined;
    const pct = (v - lo) / (hi - lo);
    if (lowBetter) return pct < 0.2 ? "#22c55e" : pct > 0.8 ? "#ef4444" : undefined;
    return pct > 0.8 ? "#22c55e" : pct < 0.2 ? "#ef4444" : undefined;
  };

  const winner = sorted[0];

  return (
    <div data-testid="gen-compare">
      {/* Header bar */}
      <div style={{ display: "flex", justifyContent: "space-between",
                          alignItems: "center", marginBottom: 12, flexWrap: "wrap",
                          gap: 8 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button style={btnGhost} onClick={onBack}>← Back to constraints</button>
          <span style={{ fontSize: 12, color: "var(--pf-muted)" }}>
            {filtered.length} of {alts.length} alternatives
          </span>
          <select data-testid="gen-filter"
              style={{ ...btnGhost, fontSize: 11, padding: "4px 8px" }}
              value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="all">All</option>
            <option value="within_budget">Within budget</option>
            <option value="within_schedule">Within schedule</option>
            <option value="top_3">Top 3</option>
            <option value="hybrids">Hybrids only</option>
          </select>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button data-testid="gen-sxs-open" style={btnGhost}
              disabled={alts.length < 2}
              onClick={onOpenSxs}>↔ Side-by-side</button>
          <button data-testid="gen-hybrid-open" style={btnGhost}
              disabled={alts.length < 2}
              onClick={onOpenHybrid}>⌥ Mix &amp; Match</button>
          <button data-testid="gen-load-report-md" style={btnGhost}
              onClick={loadReport}>Markdown</button>
          <button data-testid="gen-download-pdf" style={btnGhost}
              onClick={onDownloadPdf}>↓ PDF</button>
          {winner && (
            <button data-testid="gen-pick-winner" style={btn}
                onClick={() => onSelect(winner.id)}>
              Adopt winner ({winner.id}) →
            </button>
          )}
        </div>
      </div>

      {/* Summary cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)",
                          gap: 12, marginBottom: 16 }}>
        <Card l="Top Alt" v={winner?.id} color="#F26B1F" />
        <Card l="Best CAPEX"
              v={`${Math.min(...alts.map(a => a.cost?.capex_INR_cr ?? 0)).toFixed(1)} cr`} />
        <Card l="Fastest"
              v={`${Math.min(...alts.map(a => a.schedule?.months ?? 0))} mo`} />
        <Card l="Top score"
              v={winner?.scores?.weighted_total?.toFixed(1)} color="#22c55e" />
      </div>

      {/* Visual cards */}
      <div style={{ ...card, padding: 14, marginBottom: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between",
                            alignItems: "center" }}>
          <div style={label}>Visual Plan Thumbnails</div>
          <span style={{ fontSize: 10, color: "var(--pf-muted)" }}>
            Click any card to open the full design (PFD / floor plans / alignment)
          </span>
        </div>
        <div style={{ display: "grid",
                            gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
                            gap: 12, marginTop: 10 }}>
          {filtered.map(a => {
            const isWin = a.id === winner?.id;
            const isHybrid = String(a.id).startsWith("ALT-H");
            return (
              <div key={a.id} data-testid={`gen-card-${a.id}`}
                    style={{ background: "var(--pf-bg)",
                                  border: `2px solid ${isWin ? "#F26B1F"
                                              : isHybrid ? "#22c55e"
                                              : "var(--pf-border)"}`,
                                  borderRadius: 6, padding: 10,
                                  cursor: "pointer", transition: "transform .15s",
                                  position: "relative" }}
                    onClick={() => { setActiveAlt(a); onOpenFullView?.(a); }}>
                {isWin && <span style={{ position: "absolute", top: 6, right: 8,
                                                          background: "#F26B1F", color: "white",
                                                          padding: "1px 8px", fontSize: 9,
                                                          borderRadius: 12, fontWeight: 700 }}>
                  ★ RECOMMENDED
                </span>}
                {isHybrid && <span style={{ position: "absolute", top: 6, right: 8,
                                                              background: "#22c55e", color: "white",
                                                              padding: "1px 8px", fontSize: 9,
                                                              borderRadius: 12, fontWeight: 700 }}>
                  ⌥ HYBRID
                </span>}
                <div style={{ fontSize: 12, fontWeight: 700 }}>
                  {a.id}{" "}
                  <span style={{ color: "#F26B1F", fontWeight: 700 }}>
                    {a.scores?.weighted_total?.toFixed(0) ?? "—"}/100
                  </span>
                </div>
                <div style={{ fontSize: 11, color: "#F26B1F", marginTop: 1, fontWeight: 500 }}>
                  {a.strategy_name || "Variant"}
                </div>
                <div style={{ height: 130, marginTop: 8, overflow: "hidden",
                                      background: "#0E1116", borderRadius: 4 }}
                      dangerouslySetInnerHTML={{ __html: thumbs[a.id] || "" }} />
                <div style={{ fontSize: 10, marginTop: 6,
                                      display: "flex", justifyContent: "space-between" }}>
                  <span><b>{a.cost?.capex_INR_cr}</b> cr CAPEX</span>
                  <span>{a.schedule?.months} months</span>
                </div>
                <div style={{ fontSize: 10, color: "var(--pf-muted)", marginTop: 2 }}>
                  OPEX {a.cost?.opex_INR_cr_per_year} cr/yr
                </div>
                <div style={{ display: "flex", gap: 4, marginTop: 8 }}>
                  <button data-testid={`gen-view-${a.id}`} style={{ ...btnGhost,
                          flex: 1, fontSize: 10, padding: "4px 6px" }}
                      onClick={(e) => { e.stopPropagation(); onOpenFullView?.(a); }}>
                    View design
                  </button>
                  <button data-testid={`gen-adopt-${a.id}`} style={{ ...btn,
                          flex: 1, fontSize: 10, padding: "4px 6px" }}
                      onClick={(e) => { e.stopPropagation(); onSelect(a.id); }}>
                    {a.id === selected ? "✓ adopted" : "Adopt"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Comparison matrix */}
      <div style={{ ...card, padding: 14, overflow: "auto" }}>
        <div style={label}>Comparison Matrix</div>
        <table style={{ width: "100%", fontSize: 11, marginTop: 6 }}>
          <thead><tr style={{ borderBottom: "1px solid var(--pf-border)" }}>
            <th style={{ textAlign: "left", padding: 6 }}>ID</th>
            <th style={{ textAlign: "left", padding: 6 }}>Strategy</th>
            <th onClick={() => setSortKey("capex")}
                  style={{ textAlign: "right", padding: 6, cursor: "pointer" }}>
              CAPEX (cr) ⇅
            </th>
            <th onClick={() => setSortKey("months")}
                  style={{ textAlign: "right", padding: 6, cursor: "pointer" }}>Months ⇅</th>
            <th style={{ textAlign: "right", padding: 6 }}>OPEX (cr/yr)</th>
            <th style={{ textAlign: "right", padding: 6 }}>Energy</th>
            <th style={{ textAlign: "right", padding: 6 }}>Safety</th>
            <th style={{ textAlign: "right", padding: 6 }}>Green</th>
            <th onClick={() => setSortKey("weighted_total")}
                  style={{ textAlign: "right", padding: 6, cursor: "pointer", color: "#F26B1F" }}>
              TOTAL ⇅
            </th>
            <th></th>
          </tr></thead>
          <tbody>
            {filtered.map(a => {
              const isWin = a.id === winner?.id;
              const isPicked = a.id === selected;
              return (
                <tr key={a.id} data-testid={`gen-alt-${a.id}`}
                      style={{ background: isWin ? "rgba(242,107,31,.05)"
                                                  : isPicked ? "rgba(34,197,94,.08)" : undefined,
                                    borderBottom: "1px solid var(--pf-border)",
                                    cursor: "pointer" }}
                      onClick={() => { setActiveAlt(a); onOpenFullView?.(a); }}>
                  <td style={{ padding: 6, fontWeight: 600 }}>
                    {a.id} {isWin && <span style={{ color: "#F26B1F", fontSize: 9 }}>★</span>}
                  </td>
                  <td style={{ padding: 6, color: "#F26B1F", fontSize: 10 }}>
                    {a.strategy_name || "—"}
                  </td>
                  <td style={{ padding: 6, textAlign: "right",
                                      color: cellColor(a.cost.capex_INR_cr, capLo, capHi) }}>
                    {a.cost.capex_INR_cr}
                  </td>
                  <td style={{ padding: 6, textAlign: "right",
                                      color: cellColor(a.schedule.months, mLo, mHi) }}>
                    {a.schedule.months}
                  </td>
                  <td style={{ padding: 6, textAlign: "right" }}>{a.cost.opex_INR_cr_per_year}</td>
                  <td style={{ padding: 6, textAlign: "right" }}>{a.scores?.energy_efficiency ?? "—"}</td>
                  <td style={{ padding: 6, textAlign: "right" }}>{a.scores?.safety_margin ?? "—"}</td>
                  <td style={{ padding: 6, textAlign: "right" }}>{a.scores?.green_score ?? "—"}</td>
                  <td style={{ padding: 6, textAlign: "right",
                                      color: "#F26B1F", fontWeight: 700 }}>
                    {a.scores?.weighted_total ?? "—"}
                  </td>
                  <td style={{ padding: 6 }}>
                    <button data-testid={`gen-select-${a.id}`}
                        style={{ ...btnGhost, fontSize: 10, padding: "2px 8px" }}
                        onClick={(e) => { e.stopPropagation(); onSelect(a.id); }}>
                      {isPicked ? "✓ adopted" : "Adopt"}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* AI refine */}
      <div style={{ ...card, padding: 14, marginTop: 12,
                          border: "1px solid #F26B1F" }}>
        <div style={{ display: "flex", justifyContent: "space-between",
                            alignItems: "center" }}>
          <div style={label}>Refine with AI · Wave Q.2</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <MicButton testId="gen-refine-mic" size="sm"
                onTranscript={(t) => setRefineCmd(prev => prev ? prev + " " + t : t)}
                onAutoSubmit={(t) => { setRefineCmd(t); runRefine(t); }} />
            <span style={{ fontSize: 9, color: "var(--pf-muted)" }}>
              Powered by Capexara Process Industry AI · mutates one alt and re-scores the batch
            </span>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          <input data-testid="gen-refine-input" style={{ ...inputStyle, flex: 1, fontSize: 12 }}
              placeholder={'e.g. "switch ALT-04 HVAC to chilled-water" or "make ALT-02 use precast hybrid structure"'}
              value={refineCmd}
              onChange={(e) => setRefineCmd(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") runRefine(); }}
              disabled={refining} />
          <button data-testid="gen-refine-run" style={btn}
              onClick={runRefine} disabled={refining || !refineCmd.trim()}>
            {refining ? "Refining…" : "Apply →"}
          </button>
        </div>
        {refineErr && <div style={{ marginTop: 6, color: "#FF8888", fontSize: 10 }}>{refineErr}</div>}
        <div style={{ display: "flex", gap: 4, marginTop: 8, flexWrap: "wrap" }}>
          {[
            "Switch winner's HVAC to chilled-water",
            "Use precast hybrid structure on ALT-02",
            "Change ALT-03 form to single tower",
            "Make ALT-01 facade exposed concrete",
          ].map((s, i) => (
            <button key={i} data-testid={`gen-refine-sample-${i}`}
                style={{ ...btnGhost, fontSize: 9, padding: "3px 6px" }}
                onClick={() => setRefineCmd(s)}>{s}</button>
          ))}
        </div>
        {refineLog.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <div style={{ ...label, fontSize: 9 }}>Refinement log</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 6 }}>
              {refineLog.slice(0, 10).map((r, i) => (
                <div key={i} style={{ background: "var(--pf-bg)", padding: 8,
                                                  borderRadius: 4, fontSize: 10,
                                                  border: "1px solid var(--pf-border)" }}>
                  <div style={{ color: "#F26B1F", fontWeight: 600 }}>
                    {r.ts} · {r.cmd}
                  </div>
                  <div style={{ marginTop: 2, color: "var(--pf-muted)" }}>
                    → Mutated <b>{r.alt_id}</b>: {(r.mutations || []).map(m =>
                        `${m.key} = ${typeof m.new_value === "object"
                                              ? JSON.stringify(m.new_value)
                                              : m.new_value}`).join(" · ") || "no mutations"}
                  </div>
                  {r.explanation && (
                    <div style={{ marginTop: 2, fontStyle: "italic" }}>{r.explanation}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Markdown report */}
      {reportMd && (
        <div style={{ ...card, padding: 14, marginTop: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div style={label}>Comparison Report</div>
            <a href={`data:text/markdown;charset=utf-8,${encodeURIComponent(reportMd)}`}
                download="generative-report.md"
                style={{ ...btn, textDecoration: "none" }}>↓ Download .md</a>
          </div>
          <pre style={{ background: "var(--pf-bg)", padding: 12, marginTop: 8,
                                fontSize: 10.5, lineHeight: 1.5, borderRadius: 4,
                                overflow: "auto", maxHeight: 320,
                                whiteSpace: "pre-wrap" }}>{reportMd}</pre>
        </div>
      )}
    </div>
  );
}
